import React, { useState, useEffect } from 'react';
import { Button, Modal, Input } from '@/components/ui';
import type { MaintenanceAsset, AssetStatus, AssetHealthStatus, AssetTypeCode } from '../types/assets.types';
import { useAssets } from '../hooks/useAssets';
import { useProjects } from '@/modules/projects/shared/hooks/useProjects';
import { useZones } from '@/modules/maintenance/zones/hooks/useMaintenanceZones';
import { toast } from 'sonner';

interface AssetFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset?: MaintenanceAsset | null;
  companyId: string;
}

export function AssetFormModal({ isOpen, onClose, asset, companyId }: AssetFormModalProps) {
  const isEdit = !!asset;
  const { createAsset, updateAsset, isCreating, isUpdating } = useAssets(companyId);
  const { projects = [] } = useProjects(companyId);
  const { zones = [] } = useZones(companyId);

  const [formData, setFormData] = useState<Partial<MaintenanceAsset>>({
    asset_code: '',
    asset_name: '',
    project_id: '',
    zone_id: '',
    asset_type_code: 'TREE',
    species: '',
    quantity: 1,
    unit: 'Cây',
    area_m2: 0,
    status_code: 'active',
    health_status: 'good',
    description: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (asset) {
        setFormData(asset);
      } else {
        setFormData({
          asset_code: '',
          asset_name: '',
          project_id: '',
          zone_id: '',
          asset_type_code: 'TREE',
          species: '',
          quantity: 1,
          unit: 'Cây',
          area_m2: 0,
          status_code: 'active',
          health_status: 'good',
          description: '',
          company_id: companyId,
        });
      }
    }
  }, [isOpen, asset, companyId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.project_id) {
      toast.error('Vui lòng chọn dự án cho tài sản');
      return;
    }
    try {
      if (isEdit && asset) {
        await updateAsset({ id: asset.id, updates: formData });
      } else {
        await createAsset({ ...formData, company_id: companyId });
      }
      onClose();
      toast.success(isEdit ? 'Cập nhật tài sản thành công' : 'Thêm tài sản mới thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Cập nhật Tài sản' : 'Thêm Tài sản cảnh quan mới'} size="xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Mã tài sản *"
            required
            value={formData.asset_code || ''}
            onChange={(e) => setFormData({ ...formData, asset_code: e.target.value })}
            placeholder="VD: AS-TR-001"
          />
          <Input
            label="Tên tài sản (VD: Cây Lộc Vừng) *"
            required
            value={formData.asset_name || ''}
            onChange={(e) => setFormData({ ...formData, asset_name: e.target.value })}
            placeholder="VD: Lộc Vừng trưởng thành"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Dự án *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.project_id || ''}
              onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
            >
              <option value="">-- Chọn dự án --</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Khu vực (Zone)</label>
            <select
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.zone_id || ''}
              onChange={(e) => setFormData({ ...formData, zone_id: e.target.value })}
            >
              <option value="">-- Chọn khu vực --</option>
              {zones.filter(z => z.project_id === formData.project_id).map(z => (
                <option key={z.id} value={z.id}>{z.zone_name} ({z.zone_code})</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Loại tài sản</label>
            <select
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.asset_type_code || 'TREE'}
              onChange={(e) => setFormData({ ...formData, asset_type_code: e.target.value as AssetTypeCode })}
            >
              <option value="TREE">Cây bóng mát</option>
              <option value="SHRUB">Cây bụi</option>
              <option value="GRASS">Thảm cỏ</option>
              <option value="PALM">Cây họ dừa / cau</option>
              <option value="FLOWER">Cây hoa / cây màu</option>
              <option value="IRRIGATION">Hệ thống tưới</option>
              <option value="LIGHTING">Hệ thống chiếu sáng</option>
              <option value="HARDSCAPE">Hạ tầng / Hardscape</option>
            </select>
          </div>
          <Input
            label="Chủng loại (Loài)"
            value={formData.species || ''}
            onChange={(e) => setFormData({ ...formData, species: e.target.value })}
            placeholder="VD: Barringtonia acutangula"
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              label="Số lượng"
              type="number"
              value={formData.quantity || ''}
              onChange={(e) => setFormData({ ...formData, quantity: Number(e.target.value) })}
            />
            <Input
              label="Đơn vị"
              value={formData.unit || ''}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              placeholder="Cây / m2"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Diện tích (nếu có m2)"
            type="number"
            value={formData.area_m2 || ''}
            onChange={(e) => setFormData({ ...formData, area_m2: Number(e.target.value) })}
          />
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Tình trạng sức khỏe</label>
            <select
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.health_status || 'good'}
              onChange={(e) => setFormData({ ...formData, health_status: e.target.value as AssetHealthStatus })}
            >
              <option value="good">Tốt (Khỏe mạnh)</option>
              <option value="warning">Cảnh báo (Cần chú ý)</option>
              <option value="bad">Xấu (Vấn đề / Sâu bệnh)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Trạng thái quản lý</label>
            <select
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.status_code || 'active'}
              onChange={(e) => setFormData({ ...formData, status_code: e.target.value as AssetStatus })}
            >
              <option value="active">Đang hoạt động</option>
              <option value="maintenance">Đang bảo dưỡng</option>
              <option value="inactive">Ngừng quản lý</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Ngày trồng / Lắp đặt"
            type="date"
            value={formData.planting_date ? new Date(formData.planting_date).toISOString().split('T')[0] : ''}
            onChange={(e) => setFormData({ ...formData, planting_date: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              label="Kinh độ (Lat)"
              type="number"
              step="any"
              value={formData.gps_lat || ''}
              onChange={(e) => setFormData({ ...formData, gps_lat: parseFloat(e.target.value) })}
            />
            <Input
              label="Vĩ độ (Lng)"
              type="number"
              step="any"
              value={formData.gps_lng || ''}
              onChange={(e) => setFormData({ ...formData, gps_lng: parseFloat(e.target.value) })}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Ghi chú vị trí chi tiết</label>
          <textarea
            className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-3 min-h-[80px]"
            placeholder="VD: Cách cổng chính 10m về phía Đông, cạnh vòi nước..."
            value={formData.location_note || ''}
            onChange={(e) => setFormData({ ...formData, location_note: e.target.value })}
          />
        </div>

        <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
          <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>
            Hủy
          </Button>
          <Button variant="primary" type="submit" isLoading={isPending}>
            {isEdit ? 'Lưu thay đổi' : 'Thêm tài sản'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
